/**
 * Kthimi i pajisjes — dhe pika ku dëmi kalon (ose jo) te paga.
 *
 * Dy gjëra e bëjnë këtë dialog të ndryshëm nga një formular i thjeshtë:
 *
 *   1. **Statusi pas kthimit thuhet PARA klikimit.** Një pajisje e kthyer e
 *      dëmtuar shkon në servis, jo në dispozicion — dhe ai është pikërisht
 *      vendimi që përdoruesi duhet ta shohë, jo ta zbulojë te tabela.
 *   2. **Ndalesa nuk zbritet këtu.** Kutia krijon një rresht 'Ndalesë' në
 *      gjendjen DRAFT te Rregullimet e pagës — pra kalon nëpër të njëjtin
 *      aprovim si çdo ndalesë tjetër. Kompensimi i një dëmi nuk mund të jetë
 *      një checkbox që prek pagën drejtpërdrejt.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Grid, Alert, Box, Typography, Checkbox, FormControlLabel,
  InputAdornment, CircularProgress, Stack,
} from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import StatusChip from '../common/StatusChip';
import {
  CONDITIONS, STATUSES, assetTitle, conditionLabel, conditionChip,
  isDamagedReturn, previewStatusAfterReturn, statusChip, statusLabel, dateText,
} from '../../config/assets';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ReturnAssetDialog({ open, asset, assignment, onClose, onReturn, today }) {
  const [form, setForm] = useState({
    returnedOn: '', condition: 'good', status: '', damageNote: '', damageCost: '',
    createDeduction: false, note: '', documentName: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      returnedOn: today || todayISO(),
      condition:  assignment?.condition || asset?.condition || 'good',
      status: '', damageNote: '', damageCost: '', createDeduction: false, note: '', documentName: '',
    });
  }, [open, asset, assignment, today]);

  const set  = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const damaged = isDamagedReturn(form.condition);
  const nextStatus = useMemo(
    () => previewStatusAfterReturn(form.condition, form.status || undefined),
    [form.condition, form.status],
  );

  // Kutia e ndalesës nuk mbetet e klikuar pasi dëmi hiqet: përndryshe një
  // korrigjim i gjendjes ("jo, ishte e mirë") do të linte prapa një premtim
  // ndalese që nuk mund të kryhet.
  useEffect(() => {
    if (!damaged) setForm((f) => (f.createDeduction || f.damageCost ? { ...f, createDeduction: false, damageCost: '' } : f));
  }, [damaged]);

  const deductionInvalid = form.createDeduction && !(Number(form.damageCost) > 0);

  async function submit() {
    if (deductionInvalid) { setError('Ndalesa nga paga kërkon një kosto dëmi më të madhe se zero.'); return; }
    setBusy(true); setError('');
    try {
      await onReturn({
        ...form,
        status: form.status || undefined,
        damageCost: form.damageCost === '' ? null : Number(form.damageCost),
      });
    } catch (e) {
      setError(e.message || 'Kthimi dështoi.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'primary.soft', color: 'primary.main',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AssignmentReturnIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Regjistro kthimin</Typography>
          <Typography variant="caption" color="text.secondary">
            {asset ? `${asset.assetCode} · ${assetTitle(asset)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!!error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {assignment && (
          <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                <strong>{assignment.workerName || 'Punëtor i fshirë'}</strong> · dorëzuar {dateText(assignment.assignedOn)}
              </Typography>
              <Typography variant="body2" color="text.secondary">në gjendje</Typography>
              <StatusChip status={conditionChip(assignment.condition)} label={conditionLabel(assignment.condition)} size="sm" />
            </Stack>
            {!!assignment.accessories && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Pajisjet shtesë: {assignment.accessories}
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField type="date" label="Data e kthimit" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={form.returnedOn} onChange={set('returnedOn')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Gjendja në kthim" fullWidth size="small"
              value={form.condition} onChange={set('condition')}>
              {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
            </TextField>
          </Grid>

          {damaged && (
            <>
              <Grid item xs={12}>
                <TextField label="Dëmtimet e konstatuara" fullWidth size="small" multiline minRows={2}
                  value={form.damageNote} onChange={set('damageNote')}
                  placeholder="Ekrani i plasaritur në cepin e poshtëm djathtas…" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="number" label="Kosto e dëmit" fullWidth size="small"
                  value={form.damageCost} onChange={set('damageCost')}
                  InputProps={{ endAdornment: <InputAdornment position="end">{asset?.currency || 'EUR'}</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Checkbox
                      checked={form.createDeduction}
                      onChange={(e) => setForm((f) => ({ ...f, createDeduction: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2">Krijo ndalesë nga paga</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                  Shkon si draft te Rregullimet e pagës — pret aprovim.
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <TextField label="Dokument / foto dëshmuese" fullWidth size="small"
              value={form.documentName} onChange={set('documentName')}
              placeholder="Nr. i procesverbalit të kthimit" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Statusi pas kthimit" fullWidth size="small"
              value={form.status} onChange={set('status')}
              helperText="Bosh = vendoset nga gjendja.">
              <MenuItem value="">Sipas gjendjes</MenuItem>
              {STATUSES.filter((s) => ['service', 'lost', 'retired'].includes(s.key)).map((s) => (
                <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField label="Koment" fullWidth size="small" value={form.note} onChange={set('note')} />
          </Grid>
        </Grid>

        {/* Pasoja e shkruar para klikimit, jo e zbuluar pas tij. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">Pas kthimit pajisja bëhet:</Typography>
          <StatusChip status={statusChip(nextStatus)} label={statusLabel(nextStatus)} size="sm" />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit} disabled={busy || deductionInvalid}
          startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Regjistro kthimin
        </Button>
      </DialogActions>
    </Dialog>
  );
}
